/**
 * Cliente API de la vista Casas para Tesorería (Sprint 17 Fase 2).
 *
 * Consume GET /tesoreria/casas/?anio=YYYY.
 */
import { apiFetch } from './client';

// "compensacion" dejó de ser un estado de casa (rediseño 2026-06-25): se
// refleja como "C" en los meses (EstadoMes) y como el flag
// `tiene_compensacion` para el filtro.
export type EstadoCasa =
  | 'al_dia'
  | 'al_dia_pendiente_semestre'
  | 'atrasada'
  | 'suspendida';

export type EstadoMes = 'pagado' | 'no_pagado' | 'compensacion' | 'futuro';

export interface CasaAnualResumen {
  lote_id: string;
  identificador: string;
  // Sprint 17 S17-Z16: sector del lote (Oriente / Poniente / etc).
  sector: string | null;
  titular_nombre: string | null;
  // Sprint 17 S17-Z16: celular del titular en formato canónico
  // 9XXXXXXXX (el +56 lo agrega la UI via formatCelular).
  titular_celular: string | null;
  estado: EstadoCasa;
  saldo_total_pendiente: number;
  // Sprint 17 S17-Z5: saldo del semestre actual completo + arrastre.
  // Lo que se muestra como "Deuda" (independiente del mes corriente).
  saldo_semestre_actual: number;
  saldo_a_favor: number;
  // Saldo neto a hoy (MTD) = saldo_total_pendiente − saldo_a_favor.
  // Puede ser NEGATIVO (más crédito que deuda → se muestra verde).
  saldo_a_hoy: number;
  // Estado de cuenta del Año Anterior (reformulado 2026-06):
  // - deuda_anio_anterior: APERTURA al 1-ene (antes de abonos del año).
  // - abono_anio_anterior: abonado en el año en curso a esas deudas.
  // deuda_anio_anterior − abono_anio_anterior = saldo restante de AA.
  deuda_anio_anterior: number;
  abono_anio_anterior: number;
  ultimo_pago_fecha: string | null;
  meses: EstadoMes[];
  // True si la casa tiene ≥1 compensación activa. Alimenta el filtro
  // "Compensación"; no afecta el `estado`.
  tiene_compensacion: boolean;
}

export interface CasasAnualResponse {
  anio: number;
  casas: CasaAnualResumen[];
  conteos: Record<string, number>;
}

export function getCasasAnual(anio?: number): Promise<CasasAnualResponse> {
  const qs = anio ? `?anio=${anio}` : '';
  return apiFetch<CasasAnualResponse>(`/tesoreria/casas/${qs}`);
}

// Pieza B: resultado de aplicar el saldo a favor de un lote a sus cuotas.
export interface AplicacionCuotaResumen {
  cuota_id: string;
  periodo: string;
  monto_aplicado: number;
}

export interface AplicacionSaldoAFavorResponse {
  lote_id: string;
  credito_inicial: number;
  total_aplicado: number;
  credito_restante: number;
  cuotas: AplicacionCuotaResumen[];
}

/**
 * Aplica FIFO el saldo a favor disponible de la casa a sus cuotas abiertas
 * (reasigna crédito → cuotas, sin crear pagos). Idempotente.
 */
export function aplicarSaldoAFavor(
  loteId: string,
): Promise<AplicacionSaldoAFavorResponse> {
  return apiFetch<AplicacionSaldoAFavorResponse>(
    `/tesoreria/casas/${loteId}/aplicar-saldo-a-favor`,
    { method: 'POST' },
  );
}
